import React, { useEffect, useState } from "react";
import { TimePicker, Checkbox, Row, Col, Typography, Table, Tag, Tooltip, Button, Card, Dropdown, Flex, List, Space, notification, Modal, Empty } from "antd";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import dayjs, { Dayjs } from "dayjs";
import { PlusOutlined, DeleteOutlined, MoreOutlined, PlusCircleFilled } from "@ant-design/icons";
import FormSetup from "../../../../components/Forms/FormSetup";
import HeadingSubHeading from "../../../../components/ViewHeading/HeadingSubHeading";
import InfiniteScrollList from "../../../../components/Forms/ListView/InfiniteScrollList";
import { MasterConfigs } from "../../../../interfaces/entity";
import { useDispatch } from "react-redux";
import { postData, putData } from "../../../../actions/developer";
import { deleteRow, getData } from "../../../../actions/apiCall";
import { TableColumnProps } from "antd/lib";
const { confirm } = Modal;
const { Title, Text } = Typography;

interface Break {
    start: Dayjs;
    end: Dayjs;
}

const ShiftsCalander = () => {

    const [sameForAllDays, setSameForAllDays] = useState<boolean>(false);
    const [load, setLoad] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedShift, setselectedShift] = useState(null);
    const [shiftId, setShiftId] = useState(null);
    const dispatch = useDispatch();

    const calculateDuration = (start: string, end: string) => {
        const startTime = new Date(start);
        const endTime = new Date(end);
        return (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60); // hours
    };

    const formatHoursMinutes = (totalHours: number) => {
        const hours = Math.floor(totalHours);
        const minutes = Math.round((totalHours - hours) * 60);
        return `${hours < 10 ? '0' + hours : hours}:${minutes < 10 ? '0' + minutes : minutes} Hours`;
    };

    const defaultShift = {
        start: dayjs("10:00 AM", "hh:mm A"),
        end: dayjs("07:00 PM", "hh:mm A"),
    };

    const defaultBreak: Break = {
        start: dayjs("02:00 PM", "hh:mm A"),
        end: dayjs("03:00 PM", "hh:mm A"),
    };

    const [shiftTimings, setShiftTimings] = useState<
        Record<string, { start: Dayjs; end: Dayjs }>
    >({
        Monday: { ...defaultShift },
        Tuesday: { ...defaultShift },
        Wednesday: { ...defaultShift },
        Thursday: { ...defaultShift },
        Friday: { ...defaultShift },
        Saturday: { ...defaultShift },
        Sunday: { ...defaultShift },
    });

    const [breakTimings, setBreakTimings] = useState<
        Record<string, Break[]>
    >({
        Monday: [{ ...defaultBreak }],
        Tuesday: [{ ...defaultBreak }],
        Wednesday: [{ ...defaultBreak }],
        Thursday: [{ ...defaultBreak }],
        Friday: [{ ...defaultBreak }],
        Saturday: [{ ...defaultBreak }],
        Sunday: [{ ...defaultBreak }],
    });

    const handleShiftChange = (day: string, type: "start" | "end", time: Dayjs | null) => {
        setShiftTimings((prev) => ({
            ...prev,
            [day]: {
                ...prev[day],
                [type]: time || dayjs(),
            },
        }));
    };

    const handleBreakChange = (day: string, index: number, type: "start" | "end", time: Dayjs | null) => {
        const updatedBreaks = [...breakTimings[day]];
        updatedBreaks[index][type] = time || dayjs();

        setBreakTimings((prev) => ({
            ...prev,
            [day]: updatedBreaks,
        }));
    };

    const addBreak = (day: string) => {
        setBreakTimings((prev) => ({
            ...prev,
            [day]: [...prev[day], { start: dayjs(), end: dayjs().add(1, "hour") }],
        }));
    };

    const removeBreak = (day: string, index: number) => {
        const updatedBreaks = [...breakTimings[day]];
        updatedBreaks.splice(index, 1);

        setBreakTimings((prev) => ({
            ...prev,
            [day]: updatedBreaks,
        }));
    };

    const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

    const columns: TableColumnProps[] = [
        {
            title: "Day",
            dataIndex: "day",
            key: "day",
            render: (day: string, records) => {
                if (records.shiftStart != "Off") {
                    return <Title level={5}>{day}</Title>
                } else {
                    return <Title level={5} style={{ color: 'red' }}>{day}</Title>
                }
            }
        },
        {
            title: "Shift Start",
            dataIndex: "shiftStart",
            key: "shiftStart",
            align: "center",
            render: (text: string, records) => {
                if (records.shiftStart != "Off") {
                    return <Title level={5}>{text}</Title>
                } else {
                    return <Title level={5} style={{ color: 'red' }}>{text}</Title>
                }
            }
        },
        {
            title: "Shift End",
            dataIndex: "shiftEnd",
            key: "shiftEnd",
            align: "center",
            render: (text: string, records) => {
                if (records.shiftStart != "Off") {
                    return <Title level={5}>{text}</Title>
                } else {
                    return <Title level={5} style={{ color: 'red' }}>{text}</Title>
                }
            }
        },
        {
            title: "Total Working Hours",
            dataIndex: "workingHours",
            key: "workingHours",
            align: "center",
            render: (text: string, records) => {
                if (records.shiftStart != "Off") {
                    return <Title level={5}>{text}</Title>
                } else {
                    return <Title level={5} style={{ color: 'red' }}>{text}</Title>
                }
            }
        },
        {
            title: "Break Timings",
            dataIndex: "breaks",
            key: "breaks",
            align: "center",
            width: 200,
            render: (breaks: { start: string; end: string }[]) => (
                breaks?.length > 0 ? <ul>
                    {breaks.map((brk, index) => (
                        <li key={index}>
                            {dayjs(brk.start).format('hh:mm A')} - {dayjs(brk.end).format('hh:mm A')}
                        </li>
                    ))}
                </ul>
                    : <Tag color="red">No Break</Tag>
            ),
        },
        {
            title: "Total Break Hours",
            dataIndex: "breakHours",
            key: "breakHours",
            align: "center",
            render: (text: string, records) => {
                if (records.shiftStart != "Off") {
                    return <Title level={5}>{text}</Title>
                } else {
                    return <Title level={5} style={{ color: 'red' }}>{text}</Title>
                }
            }
        },
    ];

    const onClose = () => {
        setIsOpen(false);
        setShiftId(null)
        setShiftTimings({
            Monday: { ...defaultShift },
            Tuesday: { ...defaultShift },
            Wednesday: { ...defaultShift },
            Thursday: { ...defaultShift },
            Friday: { ...defaultShift },
            Saturday: { ...defaultShift },
            Sunday: { ...defaultShift },
        });
        setBreakTimings({
            Monday: [{ ...defaultBreak }],
            Tuesday: [{ ...defaultBreak }],
            Wednesday: [{ ...defaultBreak }],
            Thursday: [{ ...defaultBreak }],
            Friday: [{ ...defaultBreak }],
            Saturday: [{ ...defaultBreak }],
            Sunday: [{ ...defaultBreak }],
        });
    }

    const getShift = async (id) => {
        const res = await dispatch(getData(`/shift-time/${id}`))
        if (res) {
            return res.data
        }
    }

    const setData = async (shiftId) => {
        const res = await getShift(shiftId);
        if (res) {
            setIsOpen(true);
            const shift = res;
            setselectedShift(shift);

            const _shiftTimings = {
                Monday: { start: dayjs(shift.shiftTimings.Monday.start), end: dayjs(shift.shiftTimings.Monday.end) },
                Tuesday: { start: dayjs(shift.shiftTimings.Tuesday.start), end: dayjs(shift.shiftTimings.Tuesday.end) },
                Wednesday: { start: dayjs(shift.shiftTimings.Wednesday.start), end: dayjs(shift.shiftTimings.Wednesday.end) },
                Thursday: { start: dayjs(shift.shiftTimings.Thursday.start), end: dayjs(shift.shiftTimings.Thursday.end) },
                Friday: { start: dayjs(shift.shiftTimings.Friday.start), end: dayjs(shift.shiftTimings.Friday.end) },
                Saturday: { start: dayjs(shift.shiftTimings.Saturday.start), end: dayjs(shift.shiftTimings.Saturday.end) },
                Sunday: { start: dayjs(shift.shiftTimings.Sunday.start), end: dayjs(shift.shiftTimings.Sunday.end) },
            }

            const _breakTimings = {
                Monday: shift.breakTimings.Monday.map((brk) => ({ start: dayjs(brk.start), end: dayjs(brk.end) })),
                Tuesday: shift.breakTimings.Tuesday.map((brk) => ({ start: dayjs(brk.start), end: dayjs(brk.end) })),
                Wednesday: shift.breakTimings.Wednesday.map((brk) => ({ start: dayjs(brk.start), end: dayjs(brk.end) })),
                Thursday: shift.breakTimings.Thursday.map((brk) => ({ start: dayjs(brk.start), end: dayjs(brk.end) })),
                Friday: shift.breakTimings.Friday.map((brk) => ({ start: dayjs(brk.start), end: dayjs(brk.end) })),
                Saturday: shift.breakTimings.Saturday.map((brk) => ({ start: dayjs(brk.start), end: dayjs(brk.end) })),
                Sunday: shift.breakTimings.Sunday.map((brk) => ({ start: dayjs(brk.start), end: dayjs(brk.end) })),
            }
            setShiftTimings(_shiftTimings);
            setBreakTimings(_breakTimings);
        }
    }

    useEffect(() => {
        if (shiftId) {
            setData(shiftId);

        }
    }, [shiftId])

    const onShiftSelect = async (shift) => {
        const res = await getShift(shift._id);
        if (res) setselectedShift(res)
    }

    const onsubmit = async (id, values) => {
        console.log(values, shiftTimings, breakTimings)
        let resData = null
        if (id) {
            resData = await dispatch(putData(`/shift-time/${id}`, { ...values, shiftTimings, breakTimings }))
        } else {
            resData = await dispatch(postData('/shift-time', { ...values, shiftTimings, breakTimings }))
        }
        if (resData) {
            onClose()
            setLoad(true)
            if (selectedShift) {
                onShiftSelect(selectedShift)
            }
            return resData
        } else {
            notification.error({ message: 'Error', description: 'Error in creating shift' })
        }
    }


    const formConfig: MasterConfigs = {
        api: () => '/shift-time',
        title: 'Shift Time',
        modalTitle: () => formConfig.title,
        getFormColumns: () => [
            {
                label: 'Name',
                name: 'name',
                type: 'text',
                rules: [{ required: true, message: 'Please enter name' }]
            },
            {
                label: 'Code',
                name: 'code',
                type: 'text',
                rules: [{ required: true, message: 'Please enter Code' }]
            },
            {
                label: 'Description',
                name: 'description',
                type: 'textarea',
                span: 16
            },
            {
                type: 'custom',
                span: 24,
                formItem: (e) => {
                    return <div>
                        <Title level={5}>Shift Timings</Title>
                        <Row style={{
                            marginTop: "20px",
                            marginBottom: 10,
                            fontWeight: "bold",
                            textAlign: 'center'
                        }}>
                            <Col span={4}>Days</Col>
                            <Col span={8}>Shift Timings</Col>
                            <Col span={6}>Break Duration(s)</Col>
                        </Row>

                        {days.map((day) => (
                            <div key={day} style={{ marginBottom: "20px" }}>
                                <Row gutter={16} align="middle">
                                    <Col span={4} style={{ fontWeight: "bold" }}>
                                        {day}
                                    </Col>
                                    <Col span={3}>
                                        <TimePicker
                                            value={shiftTimings[day].start}
                                            format="hh:mm A"
                                            onChange={(time) => handleShiftChange(day, "start", time)}
                                            use12Hours
                                            disabled={sameForAllDays && day !== "Monday"}
                                        />
                                    </Col>
                                    <Col span={1} style={{ textAlign: "center" }}>
                                        To
                                    </Col>
                                    <Col span={3}>
                                        <TimePicker
                                            value={shiftTimings[day].end}
                                            format="hh:mm A"
                                            onChange={(time) => handleShiftChange(day, "end", time)}
                                            use12Hours
                                            disabled={sameForAllDays && day !== "Monday"}
                                        />
                                    </Col>
                                    <Col span={10}>
                                        {breakTimings[day].map((breakTime, index) => (
                                            <Row key={index} gutter={[16, 16]} align="middle" style={{ marginBottom: (breakTimings[day].length > 1) ? 10 : 0 }}>
                                                <Col span={8}>
                                                    <TimePicker
                                                        value={breakTime.start}
                                                        format="hh:mm A"
                                                        onChange={(time) => handleBreakChange(day, index, "start", time)}
                                                        use12Hours
                                                        disabled={sameForAllDays && day !== "Monday"}
                                                    />
                                                </Col>
                                                <Col span={2} style={{ textAlign: "center" }}>
                                                    To
                                                </Col>
                                                <Col span={8}>
                                                    <TimePicker
                                                        value={breakTime.end}
                                                        format="hh:mm A"
                                                        onChange={(time) => handleBreakChange(day, index, "end", time)}
                                                        use12Hours
                                                        disabled={sameForAllDays && day !== "Monday"}
                                                    />
                                                </Col>
                                                <Col span={2}>
                                                    <Space direction="horizontal">
                                                        <Button
                                                            type="link"
                                                            danger
                                                            icon={<DeleteOutlined />}
                                                            onClick={() => removeBreak(day, index)}
                                                        />
                                                        {(index + 1) == breakTimings[day].length && <Button
                                                            type="dashed"
                                                            icon={<PlusOutlined />}
                                                            onClick={() => addBreak(day)}
                                                            disabled={sameForAllDays && day !== "Monday"}
                                                        />}
                                                    </Space>
                                                </Col>
                                            </Row>
                                        ))}
                                        {breakTimings[day].length == 0 && <Button
                                            type="dashed"
                                            onClick={() => addBreak(day)}
                                            disabled={sameForAllDays && day !== "Monday"}
                                        >Add Break</Button>}
                                    </Col>
                                </Row>
                            </div>
                        ))}
                    </div>
                }
            }
        ],
        onSubmit: (id, values) => {
            return onsubmit(id, values)
        }
    }

    const dataSource = () => days.map((day) => {
        if (selectedShift?.shiftTimings) {
            const shiftData = selectedShift?.shiftTimings[day];
            const dayBreaks = selectedShift?.breakTimings[day] || [];
            const isOff = dayBreaks.length === 1 &&
                dayjs(dayBreaks[0].start).format("hh:mm A") === dayjs(shiftData.start).format("hh:mm A") &&
                dayjs(dayBreaks[0].end).format("hh:mm A") === dayjs(shiftData.start).format("hh:mm A")

            const workingHours = isOff ? 0 : calculateDuration(shiftData.start, shiftData.end);
            const breakHours = dayBreaks.reduce((total, brk) => {
                return total + calculateDuration(brk.start, brk.end);
            }, 0);

            const adjustedWorkingHours = Math.max(workingHours - breakHours, 0);

            return {
                key: day,
                day,
                shiftStart: isOff ? "Off" : dayjs(shiftData.start).format("hh:mm A"),
                shiftEnd: isOff ? "Off" : dayjs(shiftData.end).format("hh:mm A"),
                breaks: isOff ? [] : dayBreaks.map((brk) => ({ start: brk.start, end: brk.end })),
                workingHours: formatHoursMinutes(adjustedWorkingHours),
                breakHours: formatHoursMinutes(breakHours),
            };

        } else {
            return {}
        }
    });

    const onDelete = async (id) => {
        confirm({
            title: 'Do you want to delete this shift?',
            content: 'When clicked the OK button, this shift will be deleted, and you will not be able to recover it.',
            onOk: async () => {
                const res = await dispatch(deleteRow(`/shift-time/${id}`))
                if (res) {
                    setLoad(true)
                    setselectedShift(null)
                    setShiftId(null)
                    notification.success({ message: 'Success', description: 'Shift deleted successfully' })
                } else {
                    notification.error({ message: 'Error', description: 'Error in deleting shift' })
                }
            },
            onCancel() { },
        });
    }

    return (
        <>
            <FormSetup config={formConfig} open={isOpen} onClose={onClose} id={shiftId} />
            <Flex justify="space-between">
                <HeadingSubHeading
                    title="Shifts"
                    subTitle="Manage your shift timings"
                />
                <Button type="primary" onClick={() => setIsOpen(true)}>Add Shift</Button>
            </Flex>
            <Row gutter={[16, 16]}>
                <Col span={4}>
                    <Card
                        title="Projects"
                        className="custom-card"
                    >
                        <InfiniteScrollList
                            apiUrl="/shift-time"
                            height={618}
                            itemsPerPage={20}
                            reload={load}
                            onLoaded={() => setLoad(false)}
                            renderItem={(item) => {
                                return <List.Item key={item.name}
                                    className={(selectedShift?._id == item?._id ? 'active_list' : '') + " plan-item"}
                                    style={{ paddingLeft: 20 }}
                                    onClick={() => onShiftSelect(item)}>
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        <Title style={{ marginBottom: 0, lineHeight: 1.2 }} level={5}>{item?.name}</Title>
                                        <Text>{item?.clientName}</Text>
                                    </Space>
                                </List.Item >
                            }}
                        />
                    </Card>
                </Col>
                <Col span={20}>
                    <Card>
                        <Space direction="vertical" style={{ width: '100%' }} size={[10, 10]}>
                            {selectedShift && <Flex justify="space-between">
                                <Title level={5}>{selectedShift?.name}</Title>
                                <Dropdown menu={{
                                    items: [{
                                        key: '1',
                                        label: 'Edit',
                                    },
                                    {
                                        key: '2',
                                        label: 'Delete',
                                    }
                                    ],
                                    onClick: (e) => {
                                        if (e.key == '2') {
                                            onDelete(selectedShift?._id)
                                        } else {
                                            setShiftId(selectedShift?._id)
                                        }
                                    }
                                }}>
                                    <Button icon={<MoreOutlined />} type="text" />
                                </Dropdown>
                            </Flex>}
                            {selectedShift && <Table
                                columns={columns}
                                dataSource={dataSource()}
                                pagination={false}
                                rowKey="day"
                                bordered
                            />}
                        </Space>
                        {!selectedShift && <Flex justify="center" align="center">
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Select a shift to view details" />
                        </Flex>}
                    </Card>
                </Col>
            </Row>
        </>
    );
};



export default ShiftsCalander;